const STORAGE_KEY = 'dao_express_prefill'
const PREVIOUS_STORAGE_KEY = 'gbc_express_prefill'
const IN_MEMORY_TTL_MS = 10 * 60 * 1000
let inMemoryPrefillKey: string | null = null
const inMemoryPrefillCache = new Map<string, { payload: ExpressPrefillPayload; createdAt: number }>()

export interface ExpressPrefillAddress {
  street?: string
  apartment?: string
  city?: string
  state?: string
  zip?: string
  country?: string
}

export interface ExpressPrefillApplicant {
  firstName?: string
  lastName?: string
  fullName?: string
  email?: string
  ssn?: string
  dob?: string
  phoneNumber?: string
  address?: ExpressPrefillAddress
}

export interface ExpressPrefillBeneficiary {
  id?: string
  firstName?: string
  lastName?: string
  ssn?: string
  relationship?: string
  percentage?: string
  address?: ExpressPrefillAddress
}

export interface ExpressPrefillFundingItem {
  accountId: string
  title: string
  type: string
  amount?: string
  method?: string
  otherMethod?: string
  accountTermsUrl?: string
}

export interface ExpressPrefillPayload {
  source?: 'portal' | 'apply' | string
  applicationId?: string
  email?: string
  applicant?: ExpressPrefillApplicant
  jointApplicant?: ExpressPrefillApplicant
  beneficiaries?: ExpressPrefillBeneficiary[]
  funding?: ExpressPrefillFundingItem[]
  createdAt: string
  metadata?: Record<string, unknown>
  existingCustomer?: boolean
  lookupToken?: string
  maskedPhone?: string
  maskedEmail?: string
  wantsJointApplicant?: boolean
}

function buildPrefillCacheKey(): string {
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16)
    window.crypto.getRandomValues(bytes)
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  }
  return `prefill_${Date.now().toString(36)}`
}

function readPrefillStorageKey(): string | null {
  return window.sessionStorage.getItem(STORAGE_KEY)
    || window.sessionStorage.getItem(PREVIOUS_STORAGE_KEY)
}

function clearPrefillStorageKeys(): void {
  window.sessionStorage.removeItem(STORAGE_KEY)
  window.sessionStorage.removeItem(PREVIOUS_STORAGE_KEY)
}

function pruneInMemoryPrefillCache(now = Date.now()): void {
  for (const [key, entry] of inMemoryPrefillCache.entries()) {
    if (now - entry.createdAt > IN_MEMORY_TTL_MS) {
      inMemoryPrefillCache.delete(key)
    }
  }
}

export function setExpressPrefillPayload(payload: ExpressPrefillPayload): void {
  if (typeof window === 'undefined') {return}
  try {
    pruneInMemoryPrefillCache()
    if (inMemoryPrefillKey) {
      inMemoryPrefillCache.delete(inMemoryPrefillKey)
      inMemoryPrefillKey = null
    }
    const key = buildPrefillCacheKey()
    inMemoryPrefillKey = key
    inMemoryPrefillCache.set(key, { payload, createdAt: Date.now() })
    pruneInMemoryPrefillCache()
    // Persist only an opaque reference key; payload remains memory-only.
    window.sessionStorage.setItem(STORAGE_KEY, key)
  } catch {
    // Ignore storage errors silently
  }
}

export function consumeExpressPrefillPayload(): ExpressPrefillPayload | null {
  if (typeof window === 'undefined') {return null}
  try {
    const raw = inMemoryPrefillKey ?? readPrefillStorageKey()
    if (!raw) {return null}
    inMemoryPrefillKey = null
    clearPrefillStorageKeys()
    pruneInMemoryPrefillCache()
    const entry = inMemoryPrefillCache.get(raw)
    if (!entry) {return null}
    inMemoryPrefillCache.delete(raw)
    pruneInMemoryPrefillCache()
    return entry.payload
  } catch {
    try {
      if (inMemoryPrefillKey) {
        inMemoryPrefillCache.delete(inMemoryPrefillKey)
        inMemoryPrefillKey = null
      }
      clearPrefillStorageKeys()
    } catch {
      // ignore
    }
    return null
  }
}

export function clearExpressPrefillPayload(): void {
  if (typeof window === 'undefined') {return}
  try {
    const raw = inMemoryPrefillKey ?? readPrefillStorageKey()
    if (raw) {
      inMemoryPrefillCache.delete(raw)
    }
    inMemoryPrefillKey = null
    clearPrefillStorageKeys()
    pruneInMemoryPrefillCache()
  } catch {
    // ignore
  }
}

type GenericMap = Record<string, unknown>

function toRecord(value: unknown): GenericMap | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as GenericMap)
    : null
}

export interface ExpressPrefillBuildOptions {
  applicationId?: string
  fallbackEmail?: string
}

function coerceString(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim()
  }
  if (typeof value === 'number' || typeof value === 'bigint') {
    return String(value)
  }
  return ''
}

function coerceDigits(value: unknown): string {
  return coerceString(value).replace(/\D/g, '')
}

function normalizeEmailValue(value?: string | null): string | undefined {
  const normalized = (value || '').trim().toLowerCase()
  return normalized || undefined
}

export function buildExpressPrefillPayload(
  snapshot?: GenericMap | null,
  options?: ExpressPrefillBuildOptions
): ExpressPrefillPayload | null {
  if (!snapshot?.primary_applicant) {
    return null
  }

  const primaryApplicant = (snapshot.primary_applicant || {}) as GenericMap
  const basic = (primaryApplicant.basic_info || {}) as GenericMap
  const personal = (primaryApplicant.personal_info || {}) as GenericMap
  const beneficiariesArray = Array.isArray(snapshot.beneficiaries) ? snapshot.beneficiaries : []
  // Support the current nested shape (funding.products / funding.accounts) as
  // well as the legacy top-level funding array so resumed snapshots in either
  // format keep their selected accounts.
  const fundingObj = (snapshot.funding || {}) as GenericMap
  const fundingArray = Array.isArray(snapshot.funding)
    ? snapshot.funding
    : Array.isArray(fundingObj.products)
      ? fundingObj.products
      : Array.isArray(fundingObj.accounts)
        ? fundingObj.accounts
        : []
  const meta = (snapshot.meta || {}) as GenericMap
  const currentStep = meta.current_step
  const jointApplicantRoot = toRecord(snapshot.joint_applicant)
  const jointBasic = toRecord(jointApplicantRoot?.basic_info)
  const jointPersonal = toRecord(jointApplicantRoot?.personal_info)
  const hasJointApplicant = Boolean(snapshot.has_joint_applicant) || Boolean(jointApplicantRoot)

  const firstName = coerceString(basic.firstName)
  const lastName = coerceString(basic.lastName)
  const email = normalizeEmailValue(coerceString(basic.email) || options?.fallbackEmail)
  const dob = coerceString(personal.dateOfBirth)
  const ssnDigits = coerceDigits(personal.socialSecurityNumber)
  const applicantsAddress = personal

  const beneficiaries = beneficiariesArray
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') {
        return null
      }
      const item = entry as GenericMap
      const beneficiaryAddress = (item.address || {}) as GenericMap
      const mapped: ExpressPrefillBeneficiary = {
        id: coerceString(item.id) || `prefill-beneficiary-${index}`,
        firstName: coerceString(item.firstName),
        lastName: coerceString(item.lastName),
        ssn: coerceDigits(item.ssn),
        relationship: coerceString(item.relationship),
        percentage: coerceString(item.percentage),
        address: {
          street: coerceString(beneficiaryAddress.street ?? beneficiaryAddress.streetAddress),
          apartment: coerceString(beneficiaryAddress.apartment ?? beneficiaryAddress.unit),
          city: coerceString(beneficiaryAddress.city),
          state: coerceString(beneficiaryAddress.state),
          zip: coerceString(beneficiaryAddress.zip ?? beneficiaryAddress.zipCode),
          country: coerceString(beneficiaryAddress.country ?? beneficiaryAddress.countryCode) || 'US',
        },
      }
      if (!mapped.firstName && !mapped.lastName) {
        return null
      }
      return mapped
    })
    .filter((entry): entry is ExpressPrefillBeneficiary => Boolean(entry))

  const fundingEntries = fundingArray
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null
      }
      const item = entry as GenericMap
      const accountId = coerceString(item.accountId)
      const title = coerceString(item.title)
      const type = coerceString(item.category || item.type)
      if (!accountId || !title || !type) {
        return null
      }
      const fundingItem: ExpressPrefillFundingItem = {
        accountId,
        title,
        type,
        amount: coerceString(item.amount),
        method: coerceString(item.method),
        otherMethod: coerceString(item.otherMethod),
        accountTermsUrl: coerceString(item.accountTermsUrl),
      }
      return fundingItem
    })
    .filter((entry): entry is ExpressPrefillFundingItem => Boolean(entry))

  if (!email && !firstName && !lastName) {
    return null
  }

  const jointFirstName = coerceString(jointBasic?.firstName)
  const jointLastName = coerceString(jointBasic?.lastName)
  const jointDob = coerceString(jointPersonal?.dateOfBirth)
  const jointSsn = coerceDigits(jointPersonal?.socialSecurityNumber)
  const jointPhone = coerceString(jointBasic?.phoneNumber)
  const jointEmail = normalizeEmailValue(coerceString(jointBasic?.email))
  const jointStreet = coerceString(jointPersonal?.streetAddress ?? jointPersonal?.street)
  const jointApartment = coerceString(jointPersonal?.apartmentUnit ?? jointPersonal?.apartment)
  const jointCity = coerceString(jointPersonal?.city)
  const jointState = coerceString(jointPersonal?.state)
  const jointZip = coerceString(jointPersonal?.zipCode ?? jointPersonal?.zip)
  const jointFullName = [jointFirstName, jointLastName].filter(Boolean).join(' ').trim()
  const hasJointDetails = Boolean(
    jointFirstName ||
    jointLastName ||
    jointEmail ||
    jointPhone ||
    jointDob ||
    jointSsn ||
    jointStreet ||
    jointCity ||
    jointState ||
    jointZip
  )
  const jointApplicant = hasJointApplicant || hasJointDetails
    ? {
        firstName: jointFirstName || undefined,
        lastName: jointLastName || undefined,
        fullName: jointFullName || undefined,
        email: jointEmail || undefined,
        phoneNumber: jointPhone || undefined,
        ssn: jointSsn || undefined,
        dob: jointDob || undefined,
        address: {
          street: jointStreet || undefined,
          apartment: jointApartment || undefined,
          city: jointCity || undefined,
          state: jointState || undefined,
          zip: jointZip || undefined,
          country: 'US',
        },
      }
    : undefined

  return {
    source: 'portal',
    applicationId: options?.applicationId,
    email,
    createdAt: new Date().toISOString(),
    applicant: {
      firstName,
      lastName,
      fullName: [firstName, lastName].filter(Boolean).join(' ').trim() || undefined,
      ssn: ssnDigits || undefined,
      dob: dob || undefined,
      phoneNumber: coerceString(basic.phoneNumber),
      address: {
        street: coerceString(applicantsAddress.streetAddress ?? applicantsAddress.street),
        apartment: coerceString(applicantsAddress.apartmentUnit ?? applicantsAddress.apartment),
        city: coerceString(applicantsAddress.city),
        state: coerceString(applicantsAddress.state),
        zip: coerceString(applicantsAddress.zipCode ?? applicantsAddress.zip),
      },
    },
    jointApplicant,
    beneficiaries: beneficiaries.length > 0 ? beneficiaries : undefined,
    funding: fundingEntries.length > 0 ? fundingEntries : undefined,
    metadata: {
      snapshotCurrentStep: currentStep ?? null,
    },
  }
}

